import '../styles/index.scss';

import { INLINES } from '@contentful/rich-text-types';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { PropTypes } from 'prop-types';
import React from 'react';
import { Link } from 'react-scroll';

import inEmailImage from '../assets/png/gmc-in-email.png';
import ArticleHeroImage from '../components/Article/ArticleHeroImage';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Metadata from '../components/Metadata';
import ProductHighlight from '../components/ProductHighlight';

const GetMoreCourier = ({ data: { contentfulGetMoreCourier }, location }) => {
  const {
    getMoreCourierTitle,
    gmcInAudio,
    gmcInEmail,
    gmcInPrint,
    heroImage,
    metadata,
  } = contentfulGetMoreCourier;

  const inPrintOffset = -100;
  const inAudioOffset = -100;
  const inEmailOffset = -100;
  const duration = 500;

  return (
    <>
      <Metadata data={metadata} location={location} />
      <div data-module-id="get_more_courier_link_bar">
        <Header />
        <ArticleHeroImage
          article={false}
          image={heroImage}
          text={getMoreCourierTitle}
          wistiaVideoId={heroImage.wistiaVideoId}
        />
        <div className="link-bar">
          <div className="link-bar-item">
            <Link
              activeClass="active"
              className="large"
              data-component-id="get_more_courier_link"
              duration={duration}
              offset={inPrintOffset}
              smooth="true"
              spy="true"
              to="inPrint"
            >
              {gmcInPrint.title}
            </Link>
          </div>
          <hr className="link-bar-divider" />
          <div className="link-bar-item">
            <Link
              activeClass="active"
              className="large"
              data-component-id="link_bar_item_inAudio"
              duration={duration}
              offset={inAudioOffset}
              smooth="true"
              spy="true"
              to="inAudio"
            >
              {gmcInAudio.title}
            </Link>
          </div>
          <hr className="link-bar-divider" />
          <div className="link-bar-item">
            <Link
              activeClass="active"
              className="large"
              data-component-id="link_bar_item_inEmail"
              duration={duration}
              offset={inEmailOffset}
              smooth="true"
              spy="true"
              to="inEmail"
            >
              {gmcInEmail.title}
            </Link>
          </div>
        </div>
      </div>
      <div
        className="article get-more-courier"
        data-module-id="article_get_more_courier"
      >
        <div className="gmc__inPrint" id="inPrint">
          <div className="gmc__inPrint--magazines mobile--magazines">
            {gmcInPrint.magazineCovers &&
              gmcInPrint.magazineCovers.map((cover, index) => (
                <div className={`magazine magazine-${index}`}>
                  <GatsbyImage alt="cover" image={getImage(cover)} />
                </div>
              ))}
          </div>
          <div className="gmc__inPrint--description">
            <h1>{gmcInPrint.title}</h1>
            {renderRichText(gmcInPrint.text, {
              renderNode: {
                [INLINES.HYPERLINK]: (node) => (
                  <a data-component-id="gmc_inprint_link" href={node.data.uri}>
                    {node.content[0].value}
                  </a>
                ),
              },
            })}
            <div className="gmc__inPrint--subscribe">
              <a
                className="underline animate"
                data-component-id="gmc_inprint_subscribe_link"
                href={gmcInPrint.subscribeLink.url}
                rel="noreferrer noopener"
                target="_blank"
              >
                {gmcInPrint.subscribeLink.title}
              </a>
            </div>
          </div>
          <div className="gmc__inPrint--magazines">
            {gmcInPrint.magazineCovers &&
              gmcInPrint.magazineCovers.map((cover, index) => (
                <div className={`magazine magazine-${index}`}>
                  <GatsbyImage alt="cover" image={getImage(cover)} />
                </div>
              ))}
          </div>
        </div>
        <div className="gmc__inAudio" id="inAudio">
          <div className="gmc__inAudio--description">
            <h1>{gmcInAudio.title}</h1>
            {renderRichText(gmcInAudio.text, {
              renderNode: {
                [INLINES.HYPERLINK]: (node) => (
                  <a data-component-id="gmc_inaudio_link" href={node.data.uri}>
                    {node.content[0].value}
                  </a>
                ),
              },
            })}
          </div>
          {gmcInAudio &&
            gmcInAudio.products &&
            gmcInAudio.products.map((product) => (
              <ProductHighlight columns={10} product={product} />
            ))}
        </div>
        <div className="gmc__inEmail" id="inEmail">
          <div className="gmc__inEmail--image inEmail--mobile">
            <GatsbyImage alt="Email image" image={getImage(inEmailImage)} />
          </div>
          <div className="gmc__inEmail--description">
            <h1>{gmcInEmail.title}</h1>
            {renderRichText(gmcInEmail.text, {
              renderNode: {
                [INLINES.HYPERLINK]: (node) => (
                  <a data-component-id="gmc_inemail_link" href={node.data.uri}>
                    {node.content[0].value}
                  </a>
                ),
              },
            })}
          </div>
          <div className="gmc__inEmail--image">
            <GatsbyImage alt="Email image" image={getImage(inEmailImage)} />
          </div>
          {gmcInEmail &&
            gmcInEmail.products &&
            gmcInEmail.products.map((product) => (
              <ProductHighlight columns={10} product={product} />
            ))}
        </div>
      </div>
      <Footer />
    </>
  );
};

GetMoreCourier.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
};

export const query = graphql`
  query {
    contentfulGetMoreCourier {
      getMoreCourierTitle
      heroImage {
        ...ContentfulBaseImageFragment
      }
      gmcInPrint {
        title
        text {
          raw
        }
        subscribeLink {
          title
          url
        }
        magazineCovers {
          ...ContentfulBaseImageFragment
        }
      }
      gmcInAudio {
        title
        text {
          raw
        }
        products {
          ... on Node {
            ... on ContentfulProductHighlight {
              ...ContentfulProductHighlightFragment
            }
          }
        }
      }
      gmcInEmail {
        title
        text {
          raw
        }
        products {
          ... on Node {
            ... on ContentfulProductHighlight {
              ...ContentfulProductHighlightFragment
            }
          }
        }
      }
      metadata {
        ...ContentfulMetadataFragment
      }
    }
  }
`;

export default GetMoreCourier;
